import { Fragment, useContext, useEffect, useState } from "react";

import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import { css } from "@emotion/react";
import Drawer from "@mui/material/Drawer";

import {
    ChevronLeftIcon,
    CloseIcon,
    colors,
    SettingsOutlineIcon
} from "@dotnet/telematics-design-system";

import Button, {
    BUTTON_COLORS,
    BUTTON_SIZES,
    BUTTON_VARIANTS
} from "components/DesignComponents/Button";
import ListChoice from "components/DesignComponents/ListChoice";
import Logo from "components/Logo";
import Notifications from "context/notifications/NotificationsState";
import RoutesContext from "context/routes/routesContext";
import TutorialContext from "context/tutorial/tutorialContext";
import { useWindowLocation } from "shared/hooks/useWindowLocation";
import { body3SemiBold } from "shared/styles/typography";

import Controls from "./Controls";
import { handleLogoClick } from "./functions";
import Navigation from "./Navigation";

const MobileDrawer = () => {
    const { blue700, gray100, gray200, gray900, textPlaceholder, white } =
        colors;

    const {
        isDrawerOpen,
        isTutorialActive,
        isTutorialOpen,
        setTutorialOpen,
        stepIndex,
        stepGroup
    } = useContext(TutorialContext);

    const { isMobileDrawerOpen, setMobileDrawerStatus } =
        useContext(RoutesContext);

    const navigate = useNavigate();
    const { t } = useTranslation();
    const { pathname } = useWindowLocation();

    const [isInitialView, setIsInitialView] = useState(true);

    useEffect(() => {
        isTutorialOpen && !isDrawerOpen && setMobileDrawerStatus(false);

        //eslint-disable-next-line
    }, [stepGroup, stepIndex, isTutorialOpen]);

    const handleInitialView = (val: boolean) => setIsInitialView(val);

    return (
        <Drawer
            anchor="top"
            open={isMobileDrawerOpen}
            onClose={() => setMobileDrawerStatus(false)}
            slotProps={{
                transition: {
                    onExited: () => handleInitialView(true),
                    onEntered: () => isTutorialActive && setTutorialOpen(true)
                }
            }}
            sx={{
                "& .MuiDrawer-paper": {
                    height: "100%"
                }
            }}
        >
            <div
                css={css({
                    padding: "10px 24px 10px 24px",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center"
                })}
            >
                <Logo
                    css={css({
                        cursor: "pointer"
                    })}
                    onClick={() => handleLogoClick(pathname, navigate)}
                />

                <Button
                    color={BUTTON_COLORS.White}
                    size={BUTTON_SIZES.Small}
                    variant={BUTTON_VARIANTS.IconOnly}
                    icon={
                        <CloseIcon
                            css={css({
                                color: gray900
                            })}
                        />
                    }
                    onClick={() => setMobileDrawerStatus(false)}
                />
            </div>

            {isInitialView ? (
                <div
                    css={css({
                        padding: "10px 24px"
                    })}
                >
                    <div
                        css={[
                            body3SemiBold,
                            { color: textPlaceholder, marginBottom: "4px" }
                        ]}
                    >
                        {t("General##menu")}
                    </div>

                    <Navigation />

                    <div
                        css={css({
                            "& > button": {
                                display: "flex",
                                justifyContent: "flex-start",
                                marginTop: "8px",
                                padding: "6px"
                            }
                        })}
                    >
                        <Notifications handleDrawer={setMobileDrawerStatus} />

                        <Button
                            disableRipple
                            fullWidth
                            color={BUTTON_COLORS.White}
                            size={BUTTON_SIZES.Small}
                            variant={BUTTON_VARIANTS.IconLeft}
                            icon={<SettingsOutlineIcon />}
                            onClick={() => handleInitialView(false)}
                            css={css({
                                "&:hover": {
                                    background: gray100
                                },

                                "&:active": {
                                    background: gray200
                                }
                            })}
                        >
                            {t("Button##settings")}
                        </Button>
                    </div>
                </div>
            ) : (
                <Fragment>
                    <ListChoice
                        title={t("Button##go back")}
                        iconLeft={<ChevronLeftIcon />}
                        onClick={() => handleInitialView(true)}
                        css={css({
                            background: white,
                            color: blue700
                        })}
                    />

                    <Controls
                        hideNotifications
                        handleDrawer={setMobileDrawerStatus}
                    />
                </Fragment>
            )}
        </Drawer>
    );
};

export default MobileDrawer;
